// plotField.ts — the measured ground, and everything derived from it.
//
// This module owns ONE Float32Array of elevations. The displacement texture the
// shader samples is built from it; the slope, relief, roughness and insolation
// the Sampler quotes are computed from it. There is no second copy and no
// parallel approximation, so a number in the panel cannot contradict a shadow
// on the screen. That holds when the decode runs in tileWorker.ts too: the
// worker TRANSFERS that array back and the field adopts it.
//
// Everything here is metres, degrees and watts. Scene units happen later.

import { isTilePath } from './dtmBase.ts';
import { loadBitmap } from './imageBitmap.ts';
import {
  TILE_APRON_SAMPLES, TILE_HEIGHT_SAMPLES, directionForLatLon, faceUvForDirection,
  type TileBounds, type Vec3,
} from './tileTree.ts';
import type { TileWorkerHandle } from './tileWorker';
import type { RegionProjection } from './regionFrame';
import { DAYS_PER_CENTURY, elementsAt, kepler } from '../world/ephemeris.ts';
import { J2000_JD } from '../world/clock.ts';
import { orbitPeriodS, solarDayS } from '../world/solarDay.ts';
import type { BodyRow, DustCurve } from '../world/types';
import { PV_NET_EFFICIENCY } from '../../shared/power.ts';
import { wrap180HalfOpen } from '../math/angles.ts';

/** One parent plot inside a region bake, positioned in region-local metres. */
export interface HexPlacement {
  q: number;
  r: number;
  x_m: number;
  z_m: number;
  measured_fraction: number;
  /** How this plot's ground was arrived at, as a class rather than a product
   *  name. Beyond the region footprint the low frequencies are still the real
   *  global DEM; only detail below ~1 km is inferred. The three `hirise`/`mola`
   *  spellings are what bakes before Phase 4 wrote, Mars products named on
   *  every body's plots, and are read for the regions already served. */
  source?: SourceClass | 'hirise' | 'hirise+inferred' | 'mola+synthetic';
  /** This body's own products behind that class, for a human to read. */
  source_product?: string;
  /** Path to a detail tile, or null where no finer data exists to descend into. */
  tile?: string | null;
}

export type SourceClass = 'measured' | 'measured+inferred' | 'coarse+synthetic';

/** The class a plot's `source` names, whichever spelling its bake wrote. */
export function sourceClass(source?: string): SourceClass | undefined {
  switch (source) {
    case 'hirise': return 'measured';
    case 'hirise+inferred': return 'measured+inferred';
    case 'mola+synthetic': return 'coarse+synthetic';
    case 'measured':
    case 'measured+inferred':
    case 'coarse+synthetic': return source;
    default: return undefined;
  }
}

export interface PlotMeta {
  slug: string;
  /** 'region' bakes carry many adjoining hexes on one field; absent means one. */
  kind?: 'region';
  /** Built at runtime from the body's own resident leaves rather than read
   *  from a bake (`src/atlas/pyramidPlot.ts`). No bake writes it. */
  pyramid?: boolean;
  hexes?: HexPlacement[];
  hex: {
    radius_m: number;
    across_flats_m: number;
    across_corners_m: number;
    area_km2: number;
    orientation: string;
    measured_fraction: number;
    measured_fraction_worst?: number;
    plots_by_source?: Record<string, number>;
  };
  bbox_m: { width: number; height: number };
  center_lat_deg: number;
  center_lon_deg: number;
  /**
   * The bake's OWN grid. On an equirectangular product the longitude scale is
   * a constant cos(the product's CENTER_LATITUDE), not cos(a point's own
   * latitude); a polar one is placed by `src/atlas/regionFrame.ts`. Absent on
   * a region baked before `bake_region.py` wrote it.
   */
  frame?: RegionProjection;
  elevation: { min_m: number; max_m: number; relief_m: number; datum: string };
  height_texture: { file: string; size: number; width?: number; height?: number; bits: number };
  albedo_texture: { file: string; size: number } | null;
  source_resolution_m_per_px: number;
  effective_resolution_m_per_px: number;
  /** Rock abundance measured from the ortho; see tools/dtm/rocks.py. Null when
   *  the bake did not measure it. `reliable` is the bake's own verdict on
   *  whether the count was of compact boulders or of bedform texture. */
  rocks?: {
    k: number;
    reliable: boolean;
    spread: number;
    d_limit_m: number;
    method: string;
    /** The abundance to actually scatter with: the plot's own when the count
     *  was trustworthy, otherwise the site median. */
    k_used: number;
    /** `'none'` is `tools/dtm/bake_region.py` recording that the body carries
     *  no fitted law, so nothing was counted and nothing scattered. */
    k_source: 'measured' | 'site-median' | 'none';
  } | null;
  provenance: {
    product_id: string;
    data_set_id: string;
    url: string;
    instrument: string;
    rights: string;
    note: string;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The body's own orbital and solar terms
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Everything the solar quantities below need from a body row, read out once so
 * the trig itself carries no registry dependency.
 */
export interface PlotClimate {
  readonly obliquityDeg: number;
  readonly eccentricity: number;
  /** Ls at perihelion. On Mars that is southern summer, hence the dust. */
  readonly lsPerihelionDeg: number;
  /** Solar constant at the body's own semi-major axis, W/m². */
  readonly fluxMeanWm2: number;
  /** The synodic day, seconds: how long a sol is on this body. */
  readonly solLengthS: number;
  /** Null means no dust cycle is modelled here, which is not a clear Mars. */
  readonly dust: DustCurve | null;
  /** Present only where a sol spans the body's year; see `SpinOrbit`. */
  readonly spinOrbit?: SpinOrbit;
}

/**
 * A sol that spans its body's whole year, which only a spin-orbit lock makes:
 * Mercury turns three times in two orbits, so one sol is two of its years and
 * what a site gets over it is set by the orbit and the site's longitude, not
 * by the date.
 */
export interface SpinOrbit {
  /** The body's own year, seconds. */
  readonly orbitPeriodS: number;
  readonly eccentricity: number;
  /** East longitude under the sun at perihelion, degrees: Mercury's hot pole. */
  readonly perihelionSubsolarLonDeg: number;
}

const DEG = Math.PI / 180;
/** IAU 2006, between the ecliptic a row's orbit is in and the equator its pole is in. */
const ECLIPTIC_OBLIQUITY_DEG = 23.4392911;

/**
 * A row's spin-orbit terms, or undefined where its sol is shorter than its
 * year, which is every row but Mercury's.
 *
 * The sub-solar longitude is read off the row's own pole and prime meridian at
 * the perihelion nearest J2000, in the frame `bodyFixedQuaternion`
 * (src/atlas/orrery.ts) turns the globe by. On Mercury it lands 0.14 degrees
 * from the meridian the IAU defines as under the sun at perihelion.
 */
export function spinOrbitOf(row: BodyRow, solLengthS: number): SpinOrbit | undefined {
  const o = row.orbit;
  const r = row.rotation;
  if (!o || o.parent_relative) return undefined;
  const yearS = orbitPeriodS(o);
  if (yearS === null || solLengthS < yearS) return undefined;
  // The hour angle `solSamples` turns is a prograde spin's.
  if (!(r.period_s > 0)) return undefined;
  const { pole_ra_deg: ra0, pole_dec_deg: dec0, prime_meridian_deg: w0, W_rate_deg_day: wRate } = r;
  if (ra0 === null || dec0 === null || w0 === null || wRate === null) return undefined;

  const mRatePerDay = ((o.rates.L_deg_cy - o.rates.long_peri_deg_cy) * DEG) / DAYS_PER_CENTURY;
  const dDays = -elementsAt(o, J2000_JD).mRad / mRatePerDay;
  const el = elementsAt(o, J2000_JD + dDays);
  // The sun from the body at perihelion: minus the orbit plane's x' axis,
  // ecliptic J2000 turned into equatorial.
  const cE = Math.cos(ECLIPTIC_OBLIQUITY_DEG * DEG);
  const sE = Math.sin(ECLIPTIC_OBLIQUITY_DEG * DEG);
  const sx = -el.m11;
  const sy = -el.m21 * cE + el.m31 * sE;
  const sz = -el.m21 * sE - el.m31 * cE;

  const T = dDays / DAYS_PER_CENTURY;
  const ra = (ra0 + r.pole_rates.ra_deg_per_cy * T) * DEG;
  const dec = (dec0 + r.pole_rates.dec_deg_per_cy * T) * DEG;
  const px = Math.cos(dec) * Math.cos(ra);
  const py = Math.cos(dec) * Math.sin(ra);
  const pz = Math.sin(dec);
  // W counts east from the equator's ascending node on the ICRF equator; the
  // second axis is 90 degrees east of that node, pole cross node.
  const nx = -Math.sin(ra);
  const ny = Math.cos(ra);
  const alongDeg = Math.atan2(
    sx * -pz * ny + sy * pz * nx + sz * (px * ny - py * nx),
    sx * nx + sy * ny,
  ) / DEG;
  const lon = (((alongDeg - (w0 + wRate * dDays)) % 360) + 360) % 360;
  return { orbitPeriodS: yearS, eccentricity: el.e, perihelionSubsolarLonDeg: lon };
}

/**
 * A body row's climate, failing by field name rather than defaulting.
 *
 * A body that reaches a plot with no season, no solar day or no solar constant
 * is a registry gap, and the only default available would be Mars's.
 */
export function plotClimate(row: BodyRow): PlotClimate {
  const need = <T,>(v: T | null | undefined, field: string): T => {
    if (v === null || v === undefined) {
      throw new Error(
        `body "${row.id}" has no ${field}: the plot path reads it (public/art/atlas/bodies.json)`,
      );
    }
    return v;
  };
  const season = need(row.season, 'season');
  const solLengthS = need(row.rotation.solar_day_s, 'rotation.solar_day_s');
  return {
    obliquityDeg: season.obliquity_deg,
    eccentricity: season.eccentricity,
    lsPerihelionDeg: season.ls_perihelion_deg,
    fluxMeanWm2: need(row.solar_flux_mean_W_m2, 'solar_flux_mean_W_m2'),
    solLengthS,
    dust: row.atmosphere.kind === 'data' ? row.atmosphere.dust_curve : null,
    spinOrbit: spinOrbitOf(row, solLengthS),
  };
}

/**
 * The same climate with a stand-in for whatever the row has not been given,
 * and the names of the fields it stood in for.
 *
 * The ORBIT-level sky mounts through this rather than through `plotClimate`:
 * refusing to open a body at all because its season has not been fitted is a
 * worse answer than opening it with no season, and the caller warns and flags
 * what it substituted. The stand-in is mean distance and zero declination -
 * eccentricity 0 holds the body at its own semi-major axis all year, obliquity
 * 0 holds the sun on its equator - which is the one climate that invents no
 * season rather than borrowing Mars's.
 *
 * The sol length is not a stand-in at all where `lookup` can reach the rest of
 * the registry: an unpublished solar day is DERIVED from the row's own rotation
 * and its heliocentric orbit (`src/world/solarDay.ts`), which is Mercury's
 * 175.94 days rather than the 58.65-day sidereal period that used to fill the
 * hole. The sidereal period stands in only where even that cannot answer - Sol,
 * which orbits nothing - since a zero there divides, and `solarDayDerived` says
 * which of the three the caller got.
 *
 * A DESCENT still goes through `plotClimate` and still throws: the insolation
 * a plot is scored on is the whole point of the plot, so a made-up season
 * there is a lie about the ground rather than a gap in the sky.
 */
export function softClimate(
  row: BodyRow,
  lookup: (id: string) => BodyRow | null | undefined = () => null,
): {
  readonly climate: PlotClimate;
  readonly missing: readonly string[];
  /** True where the solar day was computed here rather than read off the row. */
  readonly solarDayDerived: boolean;
} {
  const missing: string[] = [];
  const season = row.season;
  const solarDay = solarDayS(row, lookup);
  if (!season) missing.push('season');
  // Only a gap a number had to be invented for: a derived solar day is this
  // body's own answer, not a substitution to warn about.
  if (solarDay === null) missing.push('rotation.solar_day_s');
  if (row.solar_flux_mean_W_m2 === null) missing.push('solar_flux_mean_W_m2');
  const solLengthS = solarDay ?? Math.abs(row.rotation.period_s);
  return {
    climate: {
      obliquityDeg: season?.obliquity_deg ?? 0,
      eccentricity: season?.eccentricity ?? 0,
      lsPerihelionDeg: season?.ls_perihelion_deg ?? 0,
      fluxMeanWm2: row.solar_flux_mean_W_m2 ?? 0,
      solLengthS,
      dust: season && row.atmosphere.kind === 'data' ? row.atmosphere.dust_curve : null,
      // The orbit's own terms, not the season's: a sol that spans the year
      // needs no fitted season to be walked.
      spinOrbit: spinOrbitOf(row, solLengthS),
    },
    missing,
    solarDayDerived: solarDay !== null && row.rotation.solar_day_s === null,
  };
}

/** Solar declination for an areocentric longitude Ls (degrees). */
export function solarDeclinationDeg(lsDeg: number, climate: PlotClimate): number {
  const s = Math.sin((lsDeg * Math.PI) / 180) * Math.sin((climate.obliquityDeg * Math.PI) / 180);
  return (Math.asin(s) * 180) / Math.PI;
}

/**
 * Top-of-atmosphere irradiance on a surface normal to the sun, at Ls.
 * An eccentric orbit swings this across the year — five times Earth's on Mars,
 * which is ~±20%. That is not flavour; it is why a solar plot's worth depends
 * on when you ask.
 */
export function solarFluxWm2(lsDeg: number, climate: PlotClimate): number {
  const e = climate.eccentricity;
  const nu = ((lsDeg - climate.lsPerihelionDeg) * Math.PI) / 180;
  const rRel = (1 - e * e) / (1 + e * Math.cos(nu));
  return climate.fluxMeanWm2 / (rRel * rRel);
}

/**
 * Photovoltaic assumptions, so the footprint on the ground means something.
 *
 * 22% cell efficiency derated to ~19% net for dust deposition and the fact
 * that a panel out there runs cold enough to help and dusty enough to hurt.
 * These are the only invented numbers in the chain — the insolation they
 * multiply is measured off the terrain.
 */
export const PV = {
  netEfficiency: PV_NET_EFFICIENCY,
  /** Canon: Solar I at Phoenix Lake, 4.38 GWh/sol. */
  solarOneWhSol: 4.38e9,
};

/** Array area in m2 to deliver `targetWhSol` from a cell's measured insolation. */
export function arrayAreaFor(targetWhSol: number, insolationWhSol: number): number {
  return targetWhSol / Math.max(1e-6, insolationWhSol * PV.netEfficiency);
}

/**
 * Column dust optical depth at an areocentric longitude, from the body's own
 * curve. Null where no dust cycle is modelled, which is not a clear Mars: the
 * caller drops the dust term rather than substituting a baseline.
 *
 * Mars is dusty all year and filthy for part of it. Activity peaks in southern
 * spring and summer, around perihelion, when the atmosphere absorbs enough
 * insolation to drive the storms that occasionally go global. The MER and MSL
 * tau records (Lemmon et al.) put its clear season, Ls 90-180, at 0.3 to 0.5
 * and the non-storm peak near 1.0; a planet-encircling storm exceeds 4 and is
 * in no row's curve, which is a seasonal mean and not a forecast.
 *
 * A row's `width_deg` is what its CLEAR season sets rather than a fit to the
 * dusty one: a wide bump adds to its own floor 70 degrees out, which is how
 * Mars's first sigma of 72 came to read 0.68 through a clear season this
 * docstring put at 0.4, and why the row now carries 30.
 *
 * This is why the season slider dims the distance as well as the light: it is
 * the same fact twice.
 */
export function dustOpticalDepth(lsDeg: number, curve: DustCurve | null): number | null {
  if (curve === null) return null;
  const d = wrap180HalfOpen(lsDeg - curve.peak_ls_deg);
  const season = Math.exp(-(d * d) / (2 * curve.width_deg * curve.width_deg));
  return curve.tau_min + curve.tau_amplitude * season;
}

/**
 * Is the sun above the horizon at the given sol phase and latitude?
 *
 * `solPhase` is 0..1 where 0.5 is local noon. The hour angle H runs from
 * -PI (sunrise) through 0 (noon) to +PI (sunset). `decDeg` is the orrery's declination
 * when the caller has it; without it the season model answers.
 */
export function isDaytime(
  solPhase: number, latDeg: number, lsDeg: number, climate: PlotClimate, decDeg?: number,
): boolean {
  const dec = decDeg ?? solarDeclinationDeg(lsDeg, climate);
  const H = (solPhase - 0.5) * 2 * Math.PI;
  const { alt } = sunAngles(latDeg, dec, H);
  return alt > 0;
}

/**
 * Fraction of the sol that is daylight at a given latitude and season.
 *
 * Returned value is in 0..1 (e.g. ~0.5 at equinox/equator). Used by the
 * battery sizing model and the energy budget display.
 */
export function dayFraction(
  latDeg: number, lsDeg: number, climate: PlotClimate, decDeg?: number,
): number {
  const lat = (latDeg * Math.PI) / 180;
  const dec = ((decDeg ?? solarDeclinationDeg(lsDeg, climate)) * Math.PI) / 180;
  // cos(H_sunrise) = -tan(lat)*tan(dec)
  const cosH = -Math.tan(lat) * Math.tan(dec);
  if (cosH <= -1) return 1;   // polar day (midnight sun)
  if (cosH >= 1) return 0;    // polar night
  // Fraction of half-cycle that is lit: H_sunset / PI
  return Math.acos(cosH) / Math.PI;
}

/**
 * Instantaneous solar output fraction at a given moment in the sol.
 *
 * Returns 0 at night, and during the day a value proportional to sin(altitude)
 * (the cosine law for a horizontal surface). Peak is ~1.0 at local noon on a
 * equator equinox day, but the actual value depends on latitude and season.
 * The caller multiplies this by the panel's rated output to get instantaneous kW.
 */
export function solarOutputFraction(
  solPhase: number, latDeg: number, lsDeg: number, climate: PlotClimate, decDeg?: number,
): number {
  const dec = decDeg ?? solarDeclinationDeg(lsDeg, climate);
  const H = (solPhase - 0.5) * 2 * Math.PI;
  const { alt } = sunAngles(latDeg, dec, H);
  if (alt <= 0) return 0;
  // sin(altitude) gives cosine-law projection onto horizontal; normalise so
  // the sol-average of solarOutputFraction over a day matches dayFraction * <sin(alt)>.
  // We just return sin(alt) directly, which the caller can scale as needed.
  return Math.sin(alt);
}

/** Sun altitude + azimuth (radians) at a latitude, declination and hour angle. */
export function sunAngles(latDeg: number, decDeg: number, hourAngleRad: number) {
  const lat = (latDeg * Math.PI) / 180;
  const dec = (decDeg * Math.PI) / 180;
  const sinAlt =
    Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(hourAngleRad);
  const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt)));
  // The sun's east and north components with cos(lat) cancelled out of both, so
  // a pole, where east and north run along the site's own meridian, keeps its bearing.
  const az = Math.atan2(
    -Math.cos(dec) * Math.sin(hourAngleRad),
    Math.cos(lat) * Math.sin(dec) - Math.sin(lat) * Math.cos(dec) * Math.cos(hourAngleRad),
  );
  return { alt, az: az < 0 ? az + 2 * Math.PI : az };
}

/** The sun at one slice of a sol: where it stands and what it delivers. */
export interface SolSample {
  readonly alt: number;
  readonly az: number;
  readonly fluxWm2: number;
}

/**
 * One sol's sun over a site, at the midpoints of `steps` equal slices of time.
 *
 * A sol short against its year is the date's sun turned through a full circle
 * of hour angle at the date's distance and declination. A sol that spans the
 * year (`spinOrbit`) walks the orbit from perihelion instead, since the sun
 * nears, recedes and stalls in the sky within the one sol, so the longitude
 * decides the mean and the date does not.
 */
export function solSamples(
  latDeg: number, lonDeg: number, lsDeg: number, climate: PlotClimate, steps = 32,
): SolSample[] {
  const out: SolSample[] = [];
  const so = climate.spinOrbit;
  if (!so) {
    const dec = solarDeclinationDeg(lsDeg, climate);
    const flux = solarFluxWm2(lsDeg, climate);
    for (let i = 0; i < steps; i++) {
      const H = ((i + 0.5) / steps) * 2 * Math.PI - Math.PI;
      const { alt, az } = sunAngles(latDeg, dec, H);
      out.push({ alt, az, fluxWm2: flux });
    }
    return out;
  }
  const e = so.eccentricity;
  const lon0 = (lonDeg - so.perihelionSubsolarLonDeg) * DEG;
  const wrapPi = (a: number): number => a - 2 * Math.PI * Math.round(a / (2 * Math.PI));
  for (let i = 0; i < steps; i++) {
    const t = ((i + 0.5) / steps) * climate.solLengthS;
    const M = wrapPi((2 * Math.PI * t) / so.orbitPeriodS);
    const E = kepler(M, e);
    const nu = 2 * Math.atan2(Math.sqrt(1 + e) * Math.sin(E / 2), Math.sqrt(1 - e) * Math.cos(E / 2));
    const rRel = 1 - e * Math.cos(E);
    // The sub-solar point runs west a turn per sol, less however far the true
    // anomaly has run ahead of the mean one.
    const H = wrapPi(lon0 - (nu - M) + (2 * Math.PI * t) / climate.solLengthS);
    const dec = solarDeclinationDeg(climate.lsPerihelionDeg + nu / DEG, climate);
    const { alt, az } = sunAngles(latDeg, dec, H);
    out.push({ alt, az, fluxWm2: climate.fluxMeanWm2 / (rRel * rRel) });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hex geometry — ONE definition, used by the parent, the sub-plots, the overlay
// rasteriser and the Python baker alike.
// ─────────────────────────────────────────────────────────────────────────────

const SQRT3 = Math.sqrt(3);

/**
 * Inside a regular hexagon of circumradius `r`, oriented **pointy along Z** —
 * vertices at (0, ±r) and (±r·√3/2, ±r/2), flat sides facing east and west.
 * That is the orientation the parent's corner list uses, and therefore the only
 * orientation anything else in the Atlas is allowed to use.
 *
 * Two bugs lived in the hand-rolled versions this replaces:
 *
 *   · They tested `|z| <= r` as the cap. For a pointy-along-X hexagon the cap
 *     is `r·√3/2`, so `|z| <= r` never bound and the shape came out stretched —
 *     a flat top at ±r instead of a vertex at ±0.866r. Not a regular hexagon.
 *   · The remaining constraint made them pointy along **X**, i.e. 90° from the
 *     parent. A hexagon has 60° symmetry, so that reads as 30° of rotation, and
 *     it is exactly what you see when the sub-plots refuse to line up with the
 *     plot they sit inside.
 */
export function insideHexZ(dx: number, dz: number, r: number): boolean {
  const ax = Math.abs(dx);
  const az = Math.abs(dz);
  return ax <= r * (SQRT3 / 2) && az + ax / SQRT3 <= r;
}

/** 0 at the hex boundary, 1 at its centre. Used to draw the rim. */
export function hexEdgeDepth(dx: number, dz: number, r: number): number {
  const ax = Math.abs(dx);
  const az = Math.abs(dz);
  return Math.min(1 - ax / (r * (SQRT3 / 2)), 1 - (az + ax / SQRT3) / r);
}

/**
 * Lattice step for tiling pointy-along-Z hexagons of circumradius `r`:
 * neighbours sit one across-flats apart horizontally, rows are 1.5r apart and
 * offset by half a step.
 */
export function hexLatticeStep(r: number): { x: number; z: number } {
  return { x: r * SQRT3, z: r * 1.5 };
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-plot verdicts
// ─────────────────────────────────────────────────────────────────────────────

export type Verdict = 'prime' | 'workable' | 'marginal' | 'unbuildable';

/**
 * Measurement fidelity of a cell. Drives the Fog of Measurement:
 *
 *   0 orbit    MOLA only — wide band (±22), hatched fill
 *   1 stereo   HiRISE DTM — narrow band (±5), solid dim fill
 *   2 surveyed rover has driven it — exact values, full fill
 */
export type DataTier = 0 | 1 | 2;

export interface SubPlot {
  id: string;
  /** Which parent plot this cell belongs to, as axial coords. */
  parentQ?: number;
  parentR?: number;
  /** Centre in metres, hex-local: +x east across flats, +z south. */
  x: number;
  z: number;
  radius_m: number;
  elevation_m: number;
  meanSlopeDeg: number;
  maxSlopeDeg: number;
  relief_m: number;
  /** RMS deviation from the local best-fit plane — a boulder-hazard proxy. */
  roughness_m: number;
  /** Downslope compass bearing, degrees from north. */
  aspectDeg: number;
  /** Mean daily insolation, Wh/m²/sol. Recomputed when the season moves. */
  insolationWhSol: number;
  /** Fraction of daylight hours the sun clears the local horizon. */
  sunFraction: number;
  verdict: Verdict;
  score: number;
  /** Measurement tier: 0 orbit, 1 stereo, 2 surveyed. See DataTier. */
  dataTier: DataTier;
}

/** The measures a verdict is settled on. */
export interface VerdictInputs {
  meanSlopeDeg: number;
  maxSlopeDeg: number;
  roughness_m: number;
  radius_m: number;
  sunFraction: number;
}

/**
 * A cell's buildability verdict. Slope dominates - you can grade rubble, you
 * cannot grade a scarp - then roughness, then how much sun the cell keeps.
 * Roughness limits are a fraction of the cell radius, not absolute metres;
 * see `scoreCells`.
 */
export function verdictFor(p: VerdictInputs): Verdict {
  const rPrime = 0.008 * p.radius_m;
  const rWork = 0.020 * p.radius_m;
  const rMax = 0.045 * p.radius_m;
  const slopeOk = p.meanSlopeDeg < 4 && p.maxSlopeDeg < 15;
  const slopeWork = p.meanSlopeDeg < 8 && p.maxSlopeDeg < 24;
  const slopeMarginal = p.meanSlopeDeg < 14;
  if (!slopeMarginal || p.roughness_m > rMax) return 'unbuildable';
  if (slopeOk && p.roughness_m < rPrime && p.sunFraction > 0.85) return 'prime';
  if (slopeWork && p.roughness_m < rWork) return 'workable';
  return 'marginal';
}

// ─────────────────────────────────────────────────────────────────────────────
// Band computation — the Fog of Measurement
// ─────────────────────────────────────────────────────────────────────────────

/** Half-widths per tier: ±22 at orbit, ±5 at stereo, 0 at surveyed. */
const BAND_HALF: Record<DataTier, number> = { 0: 22, 1: 5, 2: 0 };

/**
 * A deterministic hash of a cell id that returns a value in [-1, 1].
 *
 * Fixed per cell so the band is stable and cannot be gamed by re-reading.
 * FNV-1a over the id's code points, collapsed to a signed fraction.
 */
function cellHash(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Unsigned 32-bit → [0, 1] → [-1, 1]
  return ((h >>> 0) / 0xffffffff) * 2 - 1;
}

export interface Band {
  /** Lower bound of the band. */
  lo: number;
  /** Upper bound of the band. */
  hi: number;
  /** The verdict the band straddles. At tier 2 this is the true verdict. */
  verdictBand: Verdict;
  /** Suffix label, e.g. "MARGINAL? 30-75" or "WORKABLE 62-71" or "PRIME 88". */
  label: string;
}

/**
 * Compute the score band a player sees for a cell at its current data tier.
 *
 * At tier 2 the band collapses to the true score. At lower tiers the band
 * is the true score offset by a hash-fixed amount within the tier's half-width,
 * clamped to [0, 100].
 */
export function bandFor(cell: SubPlot): Band {
  const half = BAND_HALF[cell.dataTier];
  if (half === 0) {
    return {
      lo: cell.score,
      hi: cell.score,
      verdictBand: cell.verdict,
      label: `${cell.verdict.toUpperCase()} ${cell.score}`,
    };
  }
  const offset = cellHash(cell.id) * half;
  const centre = Math.round(cell.score + offset);
  const lo = Math.max(0, centre - half);
  const hi = Math.min(100, centre + half);
  // The band verdict is what the midpoint would earn.
  const mid = (lo + hi) / 2;
  const verdictBand: Verdict =
    mid >= 75 ? 'prime'
    : mid >= 50 ? 'workable'
    : mid >= 25 ? 'marginal'
    : 'unbuildable';
  const q = cell.dataTier === 0 ? '?' : '';
  return {
    lo, hi, verdictBand,
    label: `${verdictBand.toUpperCase()}${q} ${lo}-${hi}`,
  };
}

/**
 * Default data tier for a cell based on its parent plot's source metadata.
 * Beyond the region footprint, where the ground is the coarse global DEM plus
 * synthesis, cells start at 0; anything the region measured starts at 1.
 */
export function defaultTier(source?: string): DataTier {
  if (sourceClass(source) === 'coarse+synthetic') return 0;
  return 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// The decode — one definition, whichever thread runs it
// ─────────────────────────────────────────────────────────────────────────────

/** One level of the height texture's box-filtered chain. */
export interface HeightMip {
  data: Float32Array<ArrayBuffer>;
  width: number;
  height: number;
}

/**
 * What the decode produces, and what a PlotField is built around.
 *
 * ArrayBuffer-backed rather than `ArrayBufferLike`, because these are the
 * buffers the worker TRANSFERS and the DataTexture uploads; a
 * SharedArrayBuffer-backed view is neither.
 */
export interface FieldBuffers {
  norm: Float32Array<ArrayBuffer>;
  height: Float32Array<ArrayBuffer>;
  mips: HeightMip[];
}

/**
 * The u16 field as the two Float32Arrays everything downstream reads.
 *
 * 4.2 M iterations and 33 MB for a 2048 tile, which is why the tile worker
 * calls this instead of the main thread. It is the same function on either
 * side, so the answer cannot depend on which thread ran it.
 */
export function decodeHeightField(
  raw: Uint16Array,
  width: number,
  texHeight: number,
  minM: number,
  reliefM: number,
): { norm: Float32Array<ArrayBuffer>; height: Float32Array<ArrayBuffer> } {
  const n = width * texHeight;
  if (raw.length !== n) {
    throw new Error(`height field is ${raw.length} samples, expected ${n}`);
  }
  const norm = new Float32Array(n);
  const height = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const q = raw[i]! / 65535;
    norm[i] = q;
    height[i] = minM + q * reliefM;
  }
  return { norm, height };
}

/**
 * Box-filtered mip chain for a height field.
 *
 * The lattice step on an 18-plot claim is ~29 m while the field is 10 m/px, so
 * the vertex shader is point-sampling a signal three times finer than its own
 * sampling rate. That is geometric aliasing, and on displaced terrain it does
 * not shimmer politely — it plants a row of tents along every ridge and makes
 * distant plots crawl as the camera moves.
 *
 * Filtering the HEIGHTS is the fix; filtering the colour afterwards cannot
 * undo a vertex that was in the wrong place.
 */
export function buildHeightMips(base: Float32Array, w: number, h: number): HeightMip[] {
  const mips: HeightMip[] = [];
  let src = base;
  let sw = w;
  let sh = h;
  while (sw > 4 && sh > 4) {
    const nw = sw >> 1;
    const nh = sh >> 1;
    const dst = new Float32Array(nw * nh);
    for (let y = 0; y < nh; y++) {
      const r0 = y * 2 * sw;
      const r1 = r0 + sw;
      const o = y * nw;
      for (let x = 0; x < nw; x++) {
        const i = x * 2;
        dst[o + x] = (src[r0 + i]! + src[r0 + i + 1]! + src[r1 + i]! + src[r1 + i + 1]!) * 0.25;
      }
    }
    mips.push({ data: dst, width: nw, height: nh });
    src = dst;
    sw = nw;
    sh = nh;
  }
  return mips;
}

/** The whole CPU decode in one call: what the worker answers a field with. */
export function buildFieldBuffers(
  raw: Uint16Array,
  width: number,
  texHeight: number,
  minM: number,
  reliefM: number,
): FieldBuffers {
  const { norm, height } = decodeHeightField(raw, width, texHeight, minM, reliefM);
  return { norm, height, mips: buildHeightMips(norm, width, texHeight) };
}

function clampIndex(i: number, n: number): number {
  return i < 0 ? 0 : i > n - 1 ? n - 1 : i;
}

/**
 * Bilinear over a row-major grid at SAMPLE coordinates, edge-clamped.
 *
 * Sample centres: s = 0 is the centre of column 0 and the grid's own edge is at
 * -0.5. The planar plot lookup and the quadtree leaf lookup both land here, so
 * the two addressings are all that can differ between them.
 */
export function bilinearSample(
  data: Float32Array, w: number, h: number, s: number, t: number,
): number {
  const x0 = Math.floor(s);
  const y0 = Math.floor(t);
  const fx = s - x0;
  const fy = t - y0;
  const xa = clampIndex(x0, w);
  const xb = clampIndex(x0 + 1, w);
  const ya = clampIndex(y0, h);
  const yb = clampIndex(y0 + 1, h);
  const h00 = data[ya * w + xa]!;
  const h10 = data[ya * w + xb]!;
  const h01 = data[yb * w + xa]!;
  const h11 = data[yb * w + xb]!;
  return (h00 * (1 - fx) + h10 * fx) * (1 - fy) + (h01 * (1 - fx) + h11 * fx) * fy;
}

/**
 * A quadtree leaf's height buffer, as `LoadedTile` carries it: metres above the
 * datum, row-major, `(samples + 2 * apron)^2`.
 *
 * Contract: docs/ATLAS-TERRAIN.md, "The runtime".
 */
export interface HeightLeaf {
  readonly bounds: TileBounds;
  readonly height: Float32Array;
  /** Samples across the tile edge, apron excluded. */
  readonly samples?: number;
  readonly apron?: number;
}

/**
 * Elevation under a body-fixed direction, from a quadtree leaf.
 *
 * The same numeric contract as `PlotField.heightAt` — metres above the datum,
 * bilinear on sample centres — addressed through the tile's equal-angle face
 * coordinates instead of region-local metres. NaN off this leaf's own face: the
 * apron reaches one sample past the tile edge, never onto a neighbour, and the
 * u/v mapping does not survive the crossing.
 */
export function leafHeightAt(leaf: HeightLeaf, dir: Vec3): number {
  const b = leaf.bounds;
  const f = faceUvForDirection(dir);
  if (f.face !== b.face) return NaN;
  const n = leaf.samples ?? TILE_HEIGHT_SAMPLES;
  const apron = leaf.apron ?? TILE_APRON_SAMPLES;
  const w = n + 2 * apron;
  if (leaf.height.length !== w * w) {
    throw new Error(`leaf height is ${leaf.height.length} samples, expected ${w * w}`);
  }
  const s = ((f.u - b.u0) / (b.u1 - b.u0)) * n + apron - 0.5;
  const t = ((f.v - b.v0) / (b.v1 - b.v0)) * n + apron - 0.5;
  return bilinearSample(leaf.height, w, w, s, t);
}

/** The same lookup from body-fixed lat/lon, radians. */
export function leafHeightAtLatLon(leaf: HeightLeaf, latRad: number, lonRad: number): number {
  return leafHeightAt(leaf, directionForLatLon(latRad, lonRad));
}

/**
 * The same elevation, decimated to the lattice the tile MESH is drawn on.
 *
 * `createTileMesh` puts a vertex every `TILE_HEIGHT_SAMPLES / quads` samples
 * of the field and draws flat triangles between them, so the field's own value
 * between two mesh vertices is NOT the ground on screen: it is up to the whole
 * sub-quad relief away from it, which on Ceres at REGIONAL is hundreds of
 * metres and is what dropped hex edges under the terrain. This reads the mesh
 * quad the point lands in and interpolates over the triangle the shared index
 * buffer actually draws — the diagonal runs from the u1/v0 corner to the u0/v1
 * one (`sharedIndex` in tileMesh.ts).
 *
 * `lattice` is the bounds the mesh's own vertices are laid out on, which is the
 * DRAWN leaf's footprint; it differs from `leaf.bounds` when a leaf under the
 * height floor borrows an ancestor's field, where the mesh still lays 32 quads
 * across its own tile and resamples the ancestor inside it.
 */
export function leafMeshHeightAt(
  leaf: HeightLeaf, dir: Vec3, quads: number, lattice?: TileBounds,
): number {
  const b = leaf.bounds;
  const f = faceUvForDirection(dir);
  const lat = lattice ?? b;
  if (f.face !== b.face || f.face !== lat.face) return NaN;
  const n = leaf.samples ?? TILE_HEIGHT_SAMPLES;
  const apron = leaf.apron ?? TILE_APRON_SAMPLES;
  const w = n + 2 * apron;
  if (leaf.height.length !== w * w) {
    throw new Error(`leaf height is ${leaf.height.length} samples, expected ${w * w}`);
  }
  const q = Math.max(1, Math.floor(quads));
  const fu = ((f.u - lat.u0) / (lat.u1 - lat.u0)) * q;
  const fv = ((f.v - lat.v0) / (lat.v1 - lat.v0)) * q;
  const iu = Math.min(q - 1, Math.max(0, Math.floor(fu)));
  const iv = Math.min(q - 1, Math.max(0, Math.floor(fv)));
  const au = Math.min(1, Math.max(0, fu - iu));
  const av = Math.min(1, Math.max(0, fv - iv));
  const su = (i: number): number => ((lat.u0 + ((lat.u1 - lat.u0) * i) / q - b.u0)
    / (b.u1 - b.u0)) * n + apron - 0.5;
  const sv = (j: number): number => ((lat.v0 + ((lat.v1 - lat.v0) * j) / q - b.v0)
    / (b.v1 - b.v0)) * n + apron - 0.5;
  const at = (i: number, j: number): number => bilinearSample(leaf.height, w, w, su(i), sv(j));
  const h10 = at(iu + 1, iv);
  const h01 = at(iu, iv + 1);
  if (au + av <= 1) {
    const h00 = at(iu, iv);
    return h00 + au * (h10 - h00) + av * (h01 - h00);
  }
  const h11 = at(iu + 1, iv + 1);
  return h10 + (au + av - 1) * (h11 - h10) + (1 - au) * (h01 - h10);
}

/** The same mesh-lattice lookup from body-fixed lat/lon, radians. */
export function leafMeshHeightAtLatLon(
  leaf: HeightLeaf, latRad: number, lonRad: number, quads: number, lattice?: TileBounds,
): number {
  return leafMeshHeightAt(leaf, directionForLatLon(latRad, lonRad), quads, lattice);
}

/**
 * Height added above the ground before marching toward the sun, so the ray
 * starts clear of the texel it stands on rather than self-occluding. Shared
 * with `dtmPlot.ts`, which interpolates this same value into its shader
 * string: the Sampler's score and the drawn shadow both march from the same
 * line, the way `scoreCells` and the shader already share the `alt > 0`
 * daylight boundary.
 */
export const MARCH_START_LIFT_M = 1.5;

// ─────────────────────────────────────────────────────────────────────────────
// The field
// ─────────────────────────────────────────────────────────────────────────────

export class PlotField {
  readonly meta: PlotMeta;
  /** Elevation in metres above the areoid, row-major, north-first. */
  readonly height: Float32Array<ArrayBuffer>;
  /** Normalised 0..1 copy — this is what becomes the GPU texture. */
  readonly norm: Float32Array<ArrayBuffer>;
  /** Texture dimensions. A region bake is a tall strip, not a square. */
  readonly width: number;
  readonly texHeight: number;
  /** Metres per texel, x and z. */
  readonly mx: number;
  readonly mz: number;

  /** Where the parent hexes sit, in region-local metres. */
  readonly placements: HexPlacement[];

  /**
   * The orthoimage fetch, started by `loadPlotField` alongside the height
   * field rather than left for the walkaround's own build to kick off at the
   * SURFACE edge. `ensurePlotMesh` awaits this instead of asking again.
   */
  albedoPromise: Promise<ImageBitmap> | null = null;

  /**
   * True once `ensurePlotMesh` has taken the bitmap out of `albedoPromise`
   * and handed it into a `THREE.Texture`, from there `DtmPlot.dispose()`
   * owns the close (dtmPlot.ts). Set synchronously in the same continuation
   * that awaits the bitmap, before any other await, so a concurrent
   * `releasePlot` sees a settled answer either way rather than racing it:
   * `false` means nobody ever will, so `atlas.ts` closes it itself when the
   * promise resolves.
   */
  albedoConsumed = false;

  private mipChain: HeightMip[] | null = null;

  /**
   * From the raw u16 field, or from buffers the tile worker already decoded.
   *
   * The worker's arrays are ADOPTED, not copied: the buffer the shader texture
   * is built from is the buffer the CPU math reads, whichever thread filled it.
   */
  constructor(meta: PlotMeta, source: Uint16Array | FieldBuffers) {
    this.meta = meta;
    this.width = meta.height_texture.width ?? meta.height_texture.size;
    this.texHeight = meta.height_texture.height ?? meta.height_texture.size;
    this.placements = meta.hexes ?? [
      { q: 0, r: 0, x_m: 0, z_m: 0, measured_fraction: meta.hex.measured_fraction },
    ];
    const n = this.width * this.texHeight;
    const { min_m, relief_m } = meta.elevation;
    if (source instanceof Uint16Array) {
      const built = decodeHeightField(source, this.width, this.texHeight, min_m, relief_m);
      this.norm = built.norm;
      this.height = built.height;
    } else {
      if (source.height.length !== n) {
        throw new Error(`height field is ${source.height.length} samples, expected ${n}`);
      }
      this.norm = source.norm;
      this.height = source.height;
      this.mipChain = source.mips;
    }
    this.mx = meta.bbox_m.width / this.width;
    this.mz = meta.bbox_m.height / this.texHeight;
  }

  /** The height texture's LOD ladder: the worker's, or built here on first use. */
  get mips(): HeightMip[] {
    return (this.mipChain ??= buildHeightMips(this.norm, this.width, this.texHeight));
  }

  /** Bilinear elevation at hex-local metres. Matches what the shader samples. */
  heightAt(x: number, z: number): number {
    const W = this.width;
    const Hh = this.texHeight;
    const u = (x / this.meta.bbox_m.width + 0.5) * W - 0.5;
    const v = (z / this.meta.bbox_m.height + 0.5) * Hh - 0.5;
    return bilinearSample(this.height, W, Hh, u, v);
  }

  /** Slope in degrees, from central differences at texel scale. */
  slopeAt(x: number, z: number): number {
    const dx = (this.heightAt(x + this.mx, z) - this.heightAt(x - this.mx, z)) / (2 * this.mx);
    const dz = (this.heightAt(x, z + this.mz) - this.heightAt(x, z - this.mz)) / (2 * this.mz);
    return (Math.atan(Math.hypot(dx, dz)) * 180) / Math.PI;
  }

  /**
   * Surface normal at texel scale, written into `out`.
   *
   * Same central differences slopeAt uses, kept as a vector rather than
   * collapsed to an angle — anything that has to SIT on the ground (a rock, a
   * pad, a mast) needs the direction, not the magnitude. Writes in place
   * because the scatter calls this tens of thousands of times per plot.
   */
  normalAt(x: number, z: number, out: { x: number; y: number; z: number }): void {
    const dx = (this.heightAt(x + this.mx, z) - this.heightAt(x - this.mx, z)) / (2 * this.mx);
    const dz = (this.heightAt(x, z + this.mz) - this.heightAt(x, z - this.mz)) / (2 * this.mz);
    const inv = 1 / Math.hypot(dx, dz, 1);
    out.x = -dx * inv;
    out.y = inv;
    out.z = -dz * inv;
  }

  /** True if the point is inside the parent hexagon. */
  insideHex(x: number, z: number): boolean {
    return insideHexZ(x, z, this.meta.hex.radius_m);
  }

  /**
   * March toward the sun and report whether it clears the local horizon.
   *
   * This is the same query the shader runs per pixel to draw the shadow. Doing
   * it here too is the point: the plot that LOOKS like it spends the morning in
   * the dark is the plot the Sampler marks down for it.
   */
  sunVisible(x: number, z: number, altRad: number, azRad: number): boolean {
    // The geometric horizon, the line scoreCells counts daylight from; only
    // the ground below may hide a sun above it.
    if (altRad <= 0) return false;
    // Azimuth 0 = north = -z, increasing eastward (+x).
    const dx = Math.sin(azRad);
    const dz = -Math.cos(azRad);
    const h0 = this.heightAt(x, z) + MARCH_START_LIFT_M;
    const tanA = Math.tan(altRad);
    const step0 = Math.max(this.mx, this.mz);
    let d = step0;
    let step = step0;
    const maxD = this.meta.hex.across_corners_m;
    while (d < maxD) {
      const px = x + dx * d;
      const pz = z + dz * d;
      if (Math.abs(px) > this.meta.bbox_m.width * 0.5 || Math.abs(pz) > this.meta.bbox_m.height * 0.5) {
        return true; // walked off the measured tile with nothing in the way
      }
      if (this.heightAt(px, pz) > h0 + d * tanA) return false;
      d += step;
      step *= 1.12;
    }
    return true;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Sub-plots
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Lay a hex lattice of child plots inside the parent, `rings` deep, keeping
   * only those that fit entirely within the parent hexagon.
   */
  buildSubPlots(rings: number): SubPlot[] {
    return [...this.surveyCells(rings)];
  }

  /**
   * The same survey, one cell at a time.
   *
   * A whole survey is 30 to 40 ms on a Jezero region and it used to land inside
   * one frame, twice - once when `loadClaim` builds the cell list and again
   * when the plot mesh measures it. The caller drains this against a time
   * budget and yields to the next frame in between; the arithmetic is the same
   * `measureSubPlot`, so a cell cannot depend on how the loop was spent.
   */
  *surveyCells(rings: number): Generator<SubPlot, void, undefined> {
    const R = this.meta.hex.radius_m;
    // Child radius so that `rings` of them span the parent with a hairline gap.
    const childR = R / (rings * 2 + 1.35);
    const step = hexLatticeStep(childR);
    // Every parent plot in the region gets its own survey. On a single-hex bake
    // `placements` is one entry at the origin, so this is the same code path.
    for (const place of this.placements) {
      for (let r = -rings * 2; r <= rings * 2; r++) {
        for (let q = -rings * 2; q <= rings * 2; q++) {
          const lx = (q + r / 2) * step.x;
          const lz = r * step.z;
          if (Math.hypot(lx, lz) > R - childR * 1.1) continue;
          if (!insideHexZ(lx, lz, R)) continue;
          yield this.measureSubPlot(
            `${place.q},${place.r}:${q},${r}`,
            place.x_m + lx,
            place.z_m + lz,
            childR,
          );
        }
      }
    }
  }

  private measureSubPlot(id: string, x: number, z: number, radius: number): SubPlot {
    // Sample a disc on a jittered-free regular lattice — enough points that the
    // statistics are stable, few enough that recomputing is instant.
    const N = 9;
    const xs: number[] = [];
    const zs: number[] = [];
    const hs: number[] = [];
    for (let j = -N; j <= N; j++) {
      for (let i = -N; i <= N; i++) {
        const px = (i / N) * radius;
        const pz = (j / N) * radius;
        if (Math.hypot(px, pz) > radius) continue;
        xs.push(px);
        zs.push(pz);
        hs.push(this.heightAt(x + px, z + pz));
      }
    }
    const n = hs.length;
    let hMin = Infinity;
    let hMax = -Infinity;
    let hSum = 0;
    for (const h of hs) {
      if (h < hMin) hMin = h;
      if (h > hMax) hMax = h;
      hSum += h;
    }
    const hMean = hSum / n;

    // Least-squares plane through the disc: h = a*x + b*z + c.
    let sxx = 0;
    let szz = 0;
    let sxz = 0;
    let sxh = 0;
    let szh = 0;
    for (let i = 0; i < n; i++) {
      const dx = xs[i]!;
      const dz = zs[i]!;
      const dh = hs[i]! - hMean;
      sxx += dx * dx;
      szz += dz * dz;
      sxz += dx * dz;
      sxh += dx * dh;
      szh += dz * dh;
    }
    const det = sxx * szz - sxz * sxz;
    const a = det !== 0 ? (szz * sxh - sxz * szh) / det : 0;
    const b = det !== 0 ? (sxx * szh - sxz * sxh) / det : 0;

    // Roughness is what the plane fails to explain — the boulder-and-rubble
    // term, which is what actually stops a foundation.
    let resid = 0;
    for (let i = 0; i < n; i++) {
      const fit = hMean + a * xs[i]! + b * zs[i]!;
      const e = hs[i]! - fit;
      resid += e * e;
    }
    const roughness = Math.sqrt(resid / n);

    const meanSlopeDeg = (Math.atan(Math.hypot(a, b)) * 180) / Math.PI;

    let maxSlope = 0;
    for (let k = 0; k < 24; k++) {
      const t = (k / 24) * Math.PI * 2;
      const s = this.slopeAt(x + Math.cos(t) * radius * 0.6, z + Math.sin(t) * radius * 0.6);
      if (s > maxSlope) maxSlope = s;
    }

    // Downslope bearing: gradient points uphill, so negate.
    let aspectDeg = (Math.atan2(a, -b) * 180) / Math.PI + 180;
    aspectDeg = ((aspectDeg % 360) + 360) % 360;

    const [pq, pr] = id.split(':')[0]!.split(',').map(Number);
    // Look up the placement's source to set the default data tier.
    const place = this.placements.find((p) => p.q === pq && p.r === pr);
    return {
      id,
      parentQ: pq,
      parentR: pr,
      x,
      z,
      radius_m: radius,
      elevation_m: hMean,
      meanSlopeDeg,
      maxSlopeDeg: maxSlope,
      relief_m: hMax - hMin,
      roughness_m: roughness,
      aspectDeg,
      insolationWhSol: 0,
      sunFraction: 0,
      verdict: 'workable',
      score: 0,
      dataTier: defaultTier(place?.source),
    };
  }

  /**
   * Integrate insolation over one sol and settle each plot's verdict.
   *
   * Cheap enough to re-run whenever the season slider moves: a few dozen sun
   * positions, each doing one horizon march per plot.
   */
  scoreSubPlots(plots: SubPlot[], lsDeg: number, climate: PlotClimate, steps = 32): void {
    for (const _ of this.scoreCells(plots, lsDeg, climate, steps)) { /* drained here */ }
  }

  /** The same scoring, one cell at a time; see `surveyCells`. */
  *scoreCells(
    plots: SubPlot[], lsDeg: number, climate: PlotClimate, steps = 32,
  ): Generator<SubPlot, void, undefined> {
    const sun = solSamples(this.meta.center_lat_deg, this.meta.center_lon_deg, lsDeg, climate, steps);
    const hoursPerStep = climate.solLengthS / 3600 / steps;

    for (const p of plots) {
      // Surface normal from the fitted plane's slope and aspect.
      const slope = (p.meanSlopeDeg * Math.PI) / 180;
      const asp = (p.aspectDeg * Math.PI) / 180;
      const nx = Math.sin(slope) * Math.sin(asp);
      const nz = -Math.sin(slope) * Math.cos(asp);
      const ny = Math.cos(slope);

      let wh = 0;
      let litSteps = 0;
      let daySteps = 0;
      for (const { alt, az, fluxWm2 } of sun) {
        if (alt <= 0) continue;
        daySteps++;
        if (!this.sunVisible(p.x, p.z, alt, az)) continue;
        litSteps++;
        // Sun direction in the same frame as the surface normal.
        const sx = Math.cos(alt) * Math.sin(az);
        const sz = -Math.cos(alt) * Math.cos(az);
        const sy = Math.sin(alt);
        const cosInc = Math.max(0, nx * sx + ny * sy + nz * sz);
        wh += fluxWm2 * cosInc * hoursPerStep;
      }
      p.insolationWhSol = wh;
      p.sunFraction = daySteps ? litSteps / daySteps : 0;

      // Verdict. Slope dominates — you can grade rubble, you cannot grade a
      // scarp — then roughness, then how much sun the plot actually keeps.
      //
      // Roughness limits are a FRACTION OF THE PLOT RADIUS, not absolute
      // metres. An absolute 1.2 m threshold sounds strict and is meaningless
      // until you ask "over what distance" — across a 356 m cell it demands
      // ground flatter than a runway, and it marked every square metre of real
      // Tharsis unbuildable. Relative limits stay honest at any plot size.
      p.verdict = verdictFor(p);

      // Score blends the constraints into one comparable number so the panel
      // can rank plots without the player doing arithmetic.
      const sTerm = Math.max(0, 1 - p.meanSlopeDeg / 12);
      const rTerm = Math.max(0, 1 - p.roughness_m / (0.030 * p.radius_m));
      const iTerm = Math.min(1, p.insolationWhSol / 3000);
      p.score = Math.round(100 * (0.45 * sTerm + 0.25 * rTerm + 0.3 * iTerm));
      yield p;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading
// ─────────────────────────────────────────────────────────────────────────────

/** Absent terrain, said in terms of what to do about it. */
function absent(url: string, cause: string): Error {
  const hint = isTilePath(url)
    ? '\nDetail tiles are not kept in the repo; set VITE_DTM_TILE_BASE to the bucket that serves them.'
    : '';
  return new Error(`${cause}: ${url}${hint}`);
}

async function fetchAsset(url: string, signal?: AbortSignal): Promise<Response> {
  const res = await fetch(url, signal ? { signal } : undefined);
  if (!res.ok) throw absent(url, `HTTP ${res.status}`);
  return res;
}

export interface LoadFieldOptions {
  /** Decode in this worker instead of on the calling thread. */
  worker?: TileWorkerHandle | null;
  /** Give the pipe back when the claim these reads belong to is superseded:
   *  dropping the RESULT still leaves megabytes in front of the read that
   *  replaced it. */
  signal?: AbortSignal;
}

export async function loadPlotField(
  baseUrl: string, opts: LoadFieldOptions = {},
): Promise<PlotField> {
  const metaUrl = `${baseUrl}/meta.json`;
  const res = await fetchAsset(metaUrl, opts.signal);
  // A 200 that is not JSON is the dev server's SPA fallback standing in for a
  // file that is not there, which otherwise reads as malformed JSON.
  if (!(res.headers.get('content-type') ?? '').includes('json')) {
    throw absent(metaUrl, 'not found (the dev server answered with index.html)');
  }
  const meta = (await res.json()) as PlotMeta;
  // Started here, beside the 2.96 MB height field, rather than as the
  // walkaround build's own first await: on a slow link the two used to race
  // for the same pipe with nothing to show for it until the build finally
  // asked, at the SURFACE edge, for an image it could have had in flight
  // since the claim was.
  const albedoPromise = meta.albedo_texture
    ? loadBitmap(`${baseUrl}/${meta.albedo_texture.file}`, opts.signal)
    : null;
  // Claimed immediately so a field nothing ever builds does not surface as an
  // unhandled rejection; the promise itself still rejects for whoever awaits it.
  albedoPromise?.catch(() => {});
  let buf: ArrayBuffer;
  try {
    buf = await (await fetchAsset(`${baseUrl}/${meta.height_texture.file}`, opts.signal))
      .arrayBuffer();
  } catch (e) {
    // The albedo bitmap can resolve before the height read fails or is
    // superseded; nothing else holds a reference to it once this throws.
    void albedoPromise?.then((bitmap) => bitmap.close()).catch(() => {});
    throw e;
  }
  // Little-endian u16, straight off disk. No canvas, so no 8-bit truncation and
  // no colour management standing between the DTM and the number on screen.
  const width = meta.height_texture.width ?? meta.height_texture.size;
  const texHeight = meta.height_texture.height ?? meta.height_texture.size;
  const { min_m, relief_m } = meta.elevation;
  // A handle whose thread has gone rejects every call, so a dead one falls
  // through to the synchronous path rather than failing every plot after it.
  let field: PlotField;
  if (opts.worker?.live) {
    // `buf` is transferred into the worker, so nothing on this side reads it again.
    const buffers = await opts.worker.decodeField({
      raw: buf, width, height: texHeight, minM: min_m, reliefM: relief_m,
    });
    field = new PlotField(meta, buffers);
  } else {
    field = new PlotField(meta, new Uint16Array(buf));
  }
  field.albedoPromise = albedoPromise;
  return field;
}
